import React, { useState, useEffect, useCallback } from 'react';
import config from "../../common/Config";
import { toast } from "sonner";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid2,
  IconButton,
  InputAdornment,
  TablePagination,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
  TableContainer,
  Skeleton,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';
import { postGetProductBrand, axiosPostProductBrandForm } from '../../../utils/productBrandApi';

const getBrandImageUrl = (row) =>
  row?.image || row?.brand_image || row?.image_url || row?.img || '';

const BrandImagePicker = ({ inputId, preview, onChange, onRemove }) => (
  <Grid2 size={{ xs: 12 }}>
    <Typography variant="body2" sx={{ mb: 1 }}>
      Brand Image
    </Typography>
    <Box sx={{ position: 'relative', width: 88, height: 88 }}>
      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 88,
          height: 88,
          borderRadius: '50%',
          overflow: 'hidden',
          cursor: 'pointer',
          border: '1px dashed rgba(145, 158, 171, 0.48)',
          bgcolor: '#f4f6f8',
        }}
      >
        <input
          type="file"
          id={inputId}
          name="image"
          accept="image/png, image/jpeg, image/jpg"
          hidden
          onChange={onChange}
        />
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt=""
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Iconify icon="mdi:camera-plus-outline" width={28} sx={{ color: '#637381' }} />
        )}
      </Box>
      {preview ? (
        <IconButton
          size="small"
          onClick={onRemove}
          sx={{
            position: 'absolute',
            top: -4,
            right: -4,
            width: 22,
            height: 22,
            bgcolor: 'error.main',
            color: '#fff',
            '&:hover': { bgcolor: 'error.dark' },
          }}
        >
          <Iconify icon="mdi:close" width={14} />
        </IconButton>
      ) : null}
    </Box>
  </Grid2>
);

const Brands = () => {
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [addBrandDialogOpen, setAddBrandDialogOpen] = useState(false);
  const [editBrandDialogOpen, setEditBrandDialogOpen] = useState(false);
  const [totalBrands, setTotalBrands] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [menuAnchor, setMenuAnchor] = useState({ el: null, row: null });
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const accessToken = localStorage.getItem('accessToken');
  const token = localStorage.getItem("token");

  const [brandFormData, setBrandFormData] = useState({
    code: '',
    name: '',
    slug: '',
    description: '',
  });

  const [brandFormErrors, setBrandFormErrors] = useState({
    code: '',
    name: '',
    description: '',
  });

  const [brandEditData, setBrandEditData] = useState({
    code: '',
    name: '',
    slug: '',
    description: '',
    bid: '',
    old_image: '',
  });
  const [addImagePreview, setAddImagePreview] = useState('');
  const [editImagePreview, setEditImagePreview] = useState('');

  const [brandEditErrors, setBrandEditErrors] = useState({
    code: '',
    name: '',
    description: '',
  });

  const fetchData = useCallback(async () => {
    if (!accessToken) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }

    setLoading(true);
    try {
      const json = await postGetProductBrand(`${config.API_URL}/get_product_brand`, token, {
        draw: page + 1,
        start: page * rowsPerPage,
        length: rowsPerPage,
        searchValue: searchValue || '',
      });

      if (json?.data && Array.isArray(json.data)) {
        setBrands(json.data);
        setTotalBrands(json.recordsTotal || json.recordsFiltered || json.data.length);
      } else {
        setBrands([]);
        setTotalBrands(0);
      }
    } catch {
      toast.error("Failed to fetch brands");
      setBrands([]);
      setTotalBrands(0);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchValue, accessToken, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setBrandFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setBrandFormErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validateBrand = (values) => {
    const errors = {};
    const trimmedCode = (values.code || '').trim();
    const trimmedName = (values.name || '').trim();
    const trimmedDescription = (values.description || '').trim();

    if (!trimmedCode) {
      errors.code = 'Code is required';
    } else if (!/^[A-Za-z0-9_-]{2,20}$/.test(trimmedCode)) {
      errors.code = '2-20 chars; letters, numbers, _ or - only';
    }

    if (!trimmedName) {
      errors.name = 'Name is required';
    } else if (trimmedName.length < 2 || trimmedName.length > 50) {
      errors.name = 'Name must be 2-50 characters';
    }

    if (trimmedDescription && trimmedDescription.length > 200) {
      errors.description = 'Max 200 characters';
    }

    return { errors, cleaned: {
      code: trimmedCode,
      name: trimmedName.replace(/\s+/g, ' '),
      slug: (values.slug || '').trim(),
      description: trimmedDescription,
    }};
  };

  const handleBrandSubmit = async (event) => {
    event.preventDefault();

    const { errors, cleaned } = validateBrand(brandFormData);
    if (Object.keys(errors).length > 0) {
      setBrandFormErrors(errors);
      return;
    }

    const formData = new FormData();
    formData.append('brand_code', cleaned.code);
    formData.append('brand_name', cleaned.name);
    formData.append('slug', cleaned.slug);
    formData.append('description', cleaned.description);
    const addInput = document.getElementById('brand_image_input');
    if (addInput && addInput.files && addInput.files[0]) {
      formData.append('image', addInput.files[0]);
      formData.append('brand_image', addInput.files[0]);
    }

    const apiBrandUrl = `${config.API_URL}/add_product_brand`;
    try {
      const data = await axiosPostProductBrandForm(apiBrandUrl, accessToken, formData);
      const msg = data.message;
      if (data.success) {
        toast.success('Brand added successfully.');
        setAddBrandDialogOpen(false);
        setBrandFormData({
          code: '',
          name: '',
          slug: '',
          description: '',
        });
        setAddImagePreview('');
        const addInputClear = document.getElementById('brand_image_input');
        if (addInputClear) addInputClear.value = '';
        setBrandFormErrors({ code: '', name: '', description: '' });
        fetchData();
      } else {
        toast.error(msg || 'Failed to add brand');
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to add brand");
      }
    }
  };

  const handleEditClick = (rowData) => {
    const existingImage = getBrandImageUrl(rowData);
    setBrandEditData({
      code: rowData.code,
      name: rowData.name,
      slug: rowData.slug,
      description: rowData.description,
      bid: rowData.id,
      old_image: existingImage,
    });
    setEditImagePreview(existingImage || '');
    setEditBrandDialogOpen(true);
  };

  const handleAddImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview(URL.createObjectURL(file));
  };

  const handleEditImageChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview(URL.createObjectURL(file));
  };

  const handleRemoveAddImage = (e) => {
    if (e) e.preventDefault();
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview('');
    const addInput = document.getElementById('brand_image_input');
    if (addInput) addInput.value = '';
  };

  const handleRemoveEditImage = (e) => {
    if (e) e.preventDefault();
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview('');
    setBrandEditData((prev) => ({ ...prev, old_image: '' }));
    const editInput = document.getElementById('brand_image_input_edit');
    if (editInput) editInput.value = '';
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setMenuAnchor({ el: null, row: null });
  };

  const handleMenuOpen = (event, row) => {
    event.stopPropagation();
    setMenuAnchor({ el: event.currentTarget, row });
  };

  const handleMenuAction = (action, row) => {
    handleMenuClose();
    if (action === 'edit') {
      handleEditClick(row);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInputValue(value);
    setSearchValue(value);
    setPage(0);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleCloseAddDialog = () => {
    setAddBrandDialogOpen(false);
    setBrandFormData({
      code: '',
      name: '',
      slug: '',
      description: '',
    });
    if (addImagePreview && addImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(addImagePreview);
    }
    setAddImagePreview('');
    const addInput = document.getElementById('brand_image_input');
    if (addInput) addInput.value = '';
    setBrandFormErrors({ code: '', name: '', description: '' });
  };

  const handleCloseEditDialog = () => {
    setEditBrandDialogOpen(false);
    setBrandEditData({
      code: '',
      name: '',
      slug: '',
      description: '',
      bid: '',
      old_image: '',
    });
    if (editImagePreview && editImagePreview.startsWith('blob:')) {
      URL.revokeObjectURL(editImagePreview);
    }
    setEditImagePreview('');
    const editInput = document.getElementById('brand_image_input_edit');
    if (editInput) editInput.value = '';
    setBrandEditErrors({ code: '', name: '', description: '' });
  };

  const handleFormEditChange = (e) => {
    const { name, value } = e.target;
    setBrandEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
    setBrandEditErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const handleBrandUpdate = async (event) => {
    event.preventDefault();

    const { errors, cleaned } = validateBrand(brandEditData);
    if (Object.keys(errors).length > 0) {
      setBrandEditErrors(errors);
      return;
    }

    const formData = new FormData();
    formData.append('brand_code', cleaned.code);
    formData.append('brand_name', cleaned.name);
    formData.append('description', cleaned.description);
    formData.append('bid', brandEditData.bid);
    formData.append('old_image', brandEditData.old_image || '');
    const editInput = document.getElementById('brand_image_input_edit');
    if (editInput && editInput.files && editInput.files[0]) {
      formData.append('image', editInput.files[0]);
      formData.append('brand_image', editInput.files[0]);
    }

    const apiBrandEditUrl = `${config.API_URL}/edit_product_brand`;
    try {
      const data = await axiosPostProductBrandForm(apiBrandEditUrl, token, formData);
      const msg = data.message;
      if (data.success) {
        toast.success('Brand updated successfully.');
        setEditBrandDialogOpen(false);
        setBrandEditData({
          code: '',
          name: '',
          description: '',
          bid: '',
          old_image: '',
        });
        setEditImagePreview('');
        const editInputClear = document.getElementById('brand_image_input_edit');
        if (editInputClear) editInputClear.value = '';
        setBrandEditErrors({ code: '', name: '', description: '' });
        fetchData();
      } else {
        toast.error(msg || 'Failed to update brand');
      }
    } catch (error) {
      if (error?.message === 'ENCRYPT_KEY_MISSING') {
        toast.error('Encryption key missing.');
      } else {
        toast.error("Failed to update brand");
      }
    }
  };

  const handleChangeStatus = async (id, status) => {
    const formData = new FormData();
    formData.append('status', status);
    formData.append('bid', id);

    const apiUrl = `${config.API_URL}/update_brand_status`;
    try {
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to change status');
      }

      const data = await response.json();
      toast.success('Status changed successfully.');
      fetchData();
    } catch (error) {
      console.error('Error status change:', error);
      toast.error("Failed to change status");
    }
  };

  return (
    <Box>
      <Dialog
        open={addBrandDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Add Brand
          </Typography>
          <IconButton onClick={handleCloseAddDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="addNewBrand" onSubmit={handleBrandSubmit} sx={{ pt: 2 }}>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Code"
                  name="code"
                  id="code"
                  value={brandFormData.code}
                  onChange={handleFormChange}
                  error={!!brandFormErrors.code}
                  helperText={brandFormErrors.code}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Name"
                  name="name"
                  id="name"
                  value={brandFormData.name}
                  onChange={handleFormChange}
                  error={!!brandFormErrors.name}
                  helperText={brandFormErrors.name}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                className='input-border text-field'
                  fullWidth
                  size="small"
                  label="Description"
                  name="description"
                  id="description"
                  value={brandFormData.description}
                  onChange={handleFormChange}
                  error={!!brandFormErrors.description}
                  helperText={brandFormErrors.description}
                />
              </Grid2>
              <BrandImagePicker
                inputId="brand_image_input"
                preview={addImagePreview}
                onChange={handleAddImageChange}
                onRemove={handleRemoveAddImage}
              />
            </Grid2>
            <input type="hidden" name="slug" id="slug" value={brandFormData.slug} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseAddDialog} className="button-variant-outlined"
          variant='outlined'
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            form="addNewBrand"
            className="button-variant-contained"
          >
            Add Brand
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editBrandDialogOpen}
        onClose={handleCloseEditDialog}
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 3, pt: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Edit Brand
          </Typography>
          <IconButton onClick={handleCloseEditDialog} sx={{ p: 0.5 }}>
            <Iconify icon="eva:close-fill" width={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 0 }}>
          <Box component="form" id="editBrand" onSubmit={handleBrandUpdate} sx={{ pt: 2 }}>
            <Grid2 container spacing={3} sx={{ mb: 3 }}>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                  fullWidth
                  className='input-border'
                  size="small"
                  label="Code"
                  name="code"
                  id="editCode"
                  value={brandEditData.code}
                  onChange={handleFormEditChange}
                  error={!!brandEditErrors.code}
                  helperText={brandEditErrors.code}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                 className='input-border'
                  fullWidth
                  size="small"
                  label="Name"
                  name="name"
                  id="editName"
                  value={brandEditData.name}
                  onChange={handleFormEditChange}
                  error={!!brandEditErrors.name}
                  helperText={brandEditErrors.name}
                  required
                />
              </Grid2>
              <Grid2 size={{ xs: 12 }}>
                <TextField
                 className='input-border'
                  fullWidth
                  size="small"
                  label="Description"
                  name="description"
                  id="editDescription"
                  value={brandEditData.description}
                  onChange={handleFormEditChange}
                  error={!!brandEditErrors.description}
                  helperText={brandEditErrors.description}
                />
              </Grid2>
              <BrandImagePicker
                inputId="brand_image_input_edit"
                preview={editImagePreview}
                onChange={handleEditImageChange}
                onRemove={handleRemoveEditImage}
              />
            </Grid2>
            <input type="hidden" name="bid" value={brandEditData.bid} />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseEditDialog}
          className='button-variant-outlined'
          variant="outlined"
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="button-variant-contained"
            type="submit"
            form="editBrand"
          >
            Edit Brand
          </Button>
        </DialogActions>
      </Dialog>

      {/* <Header /> */}
      <Box className="page-container">
          <Paper className="paper-style">
            <Box sx={{ px: 2, py: 1.2 }}>
        
              <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Grid2 size={{ xs: 12, md: 6 }}>
                <Typography className="page-title">
                  Brands
                </Typography>
                </Grid2>
                <Grid2 size={{ xs: 12, md: 6 }} gap={1} display="flex" flexDirection={{ xs: "column", md: "row"}} justifyContent="flex-end" mt={{ xs: 2, md: 0 }}>
                  <TextField
                    className="input-border"
                    size="small"
                    placeholder="Search brands..."
                    value={searchInputValue}
                    onChange={handleSearchChange}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Iconify icon="eva:search-outline"/>
                          </InputAdornment>
                        )
                      }
                    }}
                    sx={{
                      width: { xs: '100%', sm: 400 },
                    }}
                  />
                  <Button
                    variant="contained"
                    className="button-variant-contained"
                    onClick={() => setAddBrandDialogOpen(true)}
                    startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                  >
                    Add Brand
                  </Button>
                </Grid2>
              </Grid2>
            </Box>
            <TableContainer
              className="table-cell-border"
            >
              <Table
                size="medium"
                sx={{ tableLayout: "auto"}}
              >
                <TableHead>
                  <TableRow className="table-cell-header">
                    {["#", "Image", "Code", "Name", "Status", "Action"].map((header) => {
                      let align = "left";
                      if (header === "#" || header === "Image" || header === "Status" || header === "Action") {
                        align = "center";
                      }
                      return (
                        <TableCell
                          key={header}
                          align={align}
                     
                        >
                          {header}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    Array.from({ length: 5 }).map((_, index) => (
                      <TableRow key={index}>
                        <TableCell colSpan={6}>
                          <Skeleton height={40} />
                        </TableCell>
                      </TableRow>
                    ))
                  ) : brands.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                        <Typography variant="body2">
                          No records found.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    brands.map((row, index) => (
                      <TableRow
                        key={row.id}
                        hover
                        className="table-cell-style"
                        sx={{
                          "&:hover": {
                            backgroundColor: "rgba(145, 158, 171, 0.04)"
                          },
                        }}
                      >
                        <TableCell align="center">
                          {page * rowsPerPage + index + 1}
                        </TableCell>
                        <TableCell align="center">
                          {getBrandImageUrl(row) ? (
                            <Box
                              component="img"
                              src={getBrandImageUrl(row)}
                              alt=""
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                border: '1px solid rgba(145, 158, 171, 0.24)',
                              }}
                            />
                          ) : (
                            <Box
                              sx={{
                                width: 36,
                                height: 36,
                                borderRadius: '50%',
                                bgcolor: '#f4f6f8',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid rgba(145, 158, 171, 0.24)',
                              }}
                            >
                              <Iconify icon="mdi:image-outline" width={16} sx={{ color: '#637381' }} />
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="left">
                          {row.code}
                        </TableCell>
                        <TableCell align="left">
                          {row.name}
                        </TableCell>
                        <TableCell align="center">
                          <Box
                            component="label"
                            sx={{
                              position: 'relative',
                              display: 'inline-flex',
                              alignItems: 'center',
                              width: 70,
                              height: 26,
                              cursor: 'pointer',
                              margin: '0 auto',
                            }}
                          >
                            <Box
                              component="input"
                              type="checkbox"
                              checked={row.status == "1"}
                              onChange={(e) => handleChangeStatus(row.id, e.target.checked ? 1 : 0)}
                              sx={{
                                opacity: 0,
                                width: 0,
                                height: 0,
                                position: 'absolute',
                              }}
                            />
                            <Box
                              sx={{
                                position: 'relative',
                                width: '100%',
                                height: '100%',
                                backgroundColor: row.status == "1" ? '#F8991D' : '#eceeef',
                                borderRadius: '13px',
                                transition: 'all 0.3s ease',
                                boxShadow: row.status == "1"
                                  ? 'inset 0 1px 2px rgba(0, 0, 0, 0.15), inset 0 0 3px rgba(0, 0, 0, 0.2)'
                                  : 'inset 0 1px 2px rgba(0, 0, 0, 0.12), inset 0 0 2px rgba(0, 0, 0, 0.15)',
                                overflow: 'hidden',
                              }}
                            >
                              <Box
                                sx={{
                                  position: 'absolute',
                                  width: 18,
                                  height: 18,
                                  left: row.status == "1" ? 'calc(100% - 24px)' : '3px',
                                  top: '3px',
                                  backgroundColor: '#FFFFFF',
                                  borderRadius: '50%',
                                  transition: 'all 0.3s ease',
                                  boxShadow: '1px 1px 5px rgba(0, 0, 0, 0.2)',
                                  zIndex: 2,
                                }}
                              />
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  left: '8px',
                                  transform: 'translateY(-50%)',
                                  color: '#FFFFFF',
                                  fontSize: '9px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  opacity: row.status == "1" ? 1 : 0,
                                  transition: 'opacity 0.3s ease',
                                  textShadow: '0 1px rgba(0, 0, 0, 0.2)',
                                  zIndex: 1,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                ACTIVE
                              </Box>
                              <Box
                                sx={{
                                  position: 'absolute',
                                  top: '50%',
                                  right: '8px',
                                  transform: 'translateY(-50%)',
                                  color: '#aaaaaa',
                                  fontSize: '9px',
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '0.3px',
                                  opacity: row.status == "1" ? 0 : 1,
                                  transition: 'opacity 0.3s ease',
                                  textShadow: '0 1px rgba(255, 255, 255, 0.5)',
                                  zIndex: 1,
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                INACTIVE
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 1, fontSize: "0.75rem", color: "#212b36" }}>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, row)}
                            sx={{
                              color: "#637381",
                              "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.04)"
                              }
                            }}
                          >
                            <Iconify icon="eva:more-vertical-fill" width={18} height={18} />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            {brands.length > 0 && (
              <Box display="flex" justifyContent="flex-end" alignItems="center">
                <TablePagination
                  component="div"
                  count={totalBrands}
                  page={page}
                  onPageChange={handleChangePage}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  className="pagination-styles"
                />
              </Box>
            )}
          </Paper>
        </Box>
      <Menu
        anchorEl={menuAnchor.el}
        open={Boolean(menuAnchor.el)}
        onClose={handleMenuClose}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          onClick: (e) => e.stopPropagation(),
          sx: {
            minWidth: 150,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
            mt: 1,
          },
        }}
      >
        {menuAnchor.row && (
          <MenuItem onClick={() => handleMenuAction('edit', menuAnchor.row)}>
            <ListItemIcon>
              <Iconify icon="fa-regular:edit" width={20} height={20} />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
};
export default Brands;
